import type { FC, ReactNode } from 'react'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import { applyDiscount } from '@/lib/format'
import { CART_STORAGE_KEY } from '@/constants/site'

export interface CartItem {
    key: string
    productId: string
    slug: string
    name: string
    sizeId: string
    sizeLabel: string
    variant: string | null
    price: number
    discountPercent: number
    quantity: number
    maxStock: number
    packageColor: string
    accentColor: string
    image: string | null
}

export type CartItemInput = Omit<CartItem, 'key' | 'quantity'>

interface CartContextValue {
    ready: boolean
    items: Array<CartItem>
    count: number
    subtotal: number
    total: number
    drawerOpen: boolean
    lastAdded: { item: CartItem, at: number } | null
    add: (input: CartItemInput, quantity?: number) => CartItem
    update: (key: string, quantity: number) => void
    remove: (key: string) => void
    replace: (items: Array<CartItem>) => void
    clear: () => void
    openDrawer: () => void
    closeDrawer: () => void
}

const CartContext = createContext<CartContextValue | null>(null)

export const cartItemKey = (productId: string, sizeId: string, variant: string | null): string =>
    `${productId}:${sizeId}:${variant ?? ''}`

const clampQuantity = (quantity: number, maxStock: number): number => {
    const limit = maxStock > 0 ? Math.min(maxStock, 99) : 99

    return Math.max(1, Math.min(limit, Math.round(quantity) || 1))
}

const isCartItem = (value: unknown): value is CartItem => {
    if (!value || typeof value !== 'object') return false

    const item = value as Record<string, unknown>

    return typeof item.key === 'string' && typeof item.productId === 'string' && typeof item.sizeId === 'string' && typeof item.quantity === 'number'
}

export const CartProvider: FC<{ children: ReactNode }> = ({ children }): ReactNode => {
    const [items, setItems] = useState<Array<CartItem>>([])
    const [ready, setReady] = useState(false)
    const [drawerOpen, setDrawerOpen] = useState(false)
    const [lastAdded, setLastAdded] = useState<CartContextValue['lastAdded']>(null)
    const itemsRef = useRef<Array<CartItem>>([])

    const commit = useCallback((next: Array<CartItem>) => {
        itemsRef.current = next
        setItems(next)

        try {
            localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(next))
        }
        catch {
            /* storage can be full or blocked; the cart still works for this visit */
        }
    }, [])

    useEffect(() => {
        try {
            const saved = localStorage.getItem(CART_STORAGE_KEY)
            const parsed = saved ? JSON.parse(saved) : []

            if (Array.isArray(parsed)) {
                const valid = parsed.filter(isCartItem)

                itemsRef.current = valid
                setItems(valid)
            }
        }
        catch {
            /* ignore a damaged saved cart */
        }

        setReady(true)

        const onStorage = (event: StorageEvent) => {
            if (event.key !== CART_STORAGE_KEY) return

            try {
                const parsed = event.newValue ? JSON.parse(event.newValue) : []
                const valid = Array.isArray(parsed) ? parsed.filter(isCartItem) : []

                itemsRef.current = valid
                setItems(valid)
            }
            catch {
                /* ignore */
            }
        }

        window.addEventListener('storage', onStorage)

        return () => window.removeEventListener('storage', onStorage)
    }, [])

    const add = useCallback((input: CartItemInput, quantity = 1): CartItem => {
        const key = cartItemKey(input.productId, input.sizeId, input.variant)
        const current = itemsRef.current
        const existing = current.find(item => item.key === key)
        let added: CartItem

        if (existing) {
            added = { ...existing, ...input, key, quantity: clampQuantity(existing.quantity + quantity, input.maxStock) }
            commit(current.map(item => item.key === key ? added : item))
        }
        else {
            added = { ...input, key, quantity: clampQuantity(quantity, input.maxStock) }
            commit([...current, added])
        }

        setLastAdded({ item: added, at: Date.now() })

        return added
    }, [commit])

    const update = useCallback((key: string, quantity: number) => {
        commit(itemsRef.current.map(item => item.key === key ? { ...item, quantity: clampQuantity(quantity, item.maxStock) } : item))
    }, [commit])

    const remove = useCallback((key: string) => {
        commit(itemsRef.current.filter(item => item.key !== key))
    }, [commit])

    const replace = useCallback((next: Array<CartItem>) => {
        commit(next)
    }, [commit])

    const clear = useCallback(() => {
        commit([])
    }, [commit])

    const value = useMemo<CartContextValue>(() => {
        const count = items.reduce((sum, item) => sum + item.quantity, 0)
        const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0)
        const total = items.reduce((sum, item) => sum + applyDiscount(item.price, item.discountPercent) * item.quantity, 0)

        return {
            ready,
            items,
            count,
            subtotal,
            total,
            drawerOpen,
            lastAdded,
            add,
            update,
            remove,
            replace,
            clear,
            openDrawer: () => setDrawerOpen(true),
            closeDrawer: () => setDrawerOpen(false)
        }
    }, [ready, items, drawerOpen, lastAdded, add, update, remove, replace, clear])

    return (
        <CartContext.Provider value={value}>
            {children}
        </CartContext.Provider>
    )
}

export const useCart = (): CartContextValue => {
    const context = useContext(CartContext)

    if (!context) throw new Error('useCart must be used inside CartProvider')

    return context
}
